import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useProducts } from '@/hooks/useProducts';

const categories = ['Tümü', 'Laptop', 'Masaüstü', 'Monitör', 'İşlemci', 'Anakart', 'RAM', 'SSD', 'Ekran Kartı', 'Aksesuar'];

const IkinciElPage = () => {
  const { products, loading } = useProducts();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('Tümü');
  const [sortBy, setSortBy] = useState('default');
  const [search, setSearch] = useState('');
  const [visibleCards, setVisibleCards] = useState<Set<number>>(new Set());
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  let filtered = products.filter(p => {
    const matchCat = selectedCategory === 'Tümü' || p.category === selectedCategory;
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase()) || p.description.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  if (sortBy === 'price-asc') filtered = [...filtered].sort((a, b) => a.price - b.price);
  else if (sortBy === 'price-desc') filtered = [...filtered].sort((a, b) => b.price - a.price);
  else if (sortBy === 'newest') filtered = [...filtered].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  else filtered = [...filtered].sort((a, b) => a.order - b.order);

  useEffect(() => {
    setVisibleCards(new Set());
    cardRefs.current = [];
  }, [selectedCategory, sortBy, search]);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    cardRefs.current.forEach((el, i) => {
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              setVisibleCards(prev => new Set([...prev, i]));
            }, i % 4 * 60);
            obs.disconnect();
          }
        },
        { threshold: 0.1 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach(o => o.disconnect());
  }, [filtered.length]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      {/* Hero */}
      <div className="pt-16 md:pt-20 bg-slate-900 text-white py-14 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,165,0,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,165,0,0.3) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center relative z-10">
          <span className="inline-block bg-orange-500/20 text-orange-400 text-xs font-semibold px-3 py-1 rounded-full mb-3 uppercase tracking-wider border border-orange-500/30">
            İkinci El
          </span>
          <h1 className="text-3xl md:text-5xl font-extrabold mb-4">İkinci El Bilgisayar &amp; Parçalar</h1>
          <p className="text-slate-300 text-base max-w-xl mx-auto">Kalite kontrollü, test edilmiş ikinci el bilgisayar ve parçalar uygun fiyatlarla.</p>
        </div>
      </div>

      {/* Filters */}
      <div className="sticky top-16 md:top-20 z-30 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
            <input
              type="text"
              placeholder="Ürün ara..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-400"
            />
          </div>
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-400 cursor-pointer"
          >
            <option value="default">Varsayılan Sıralama</option>
            <option value="newest">En Yeni</option>
            <option value="price-asc">Fiyat: Düşükten Yükseğe</option>
            <option value="price-desc">Fiyat: Yüksekten Düşüğe</option>
          </select>
        </div>
        {/* Category Tabs */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 pb-3 flex gap-2 overflow-x-auto scrollbar-hide">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                selectedCategory === cat ? 'bg-orange-500 text-white scale-105' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Products Grid */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
        {loading ? (
          <div className="text-center py-20 text-slate-400">
            <i className="ri-loader-4-line text-5xl mb-3 block animate-spin"></i>
            <p>Ürünler yükleniyor...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-slate-400">
            <i className="ri-inbox-line text-5xl mb-3 block"></i>
            <p>Ürün bulunamadı.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered.map((product, i) => (
              <div
                key={product.id}
                ref={el => { cardRefs.current[i] = el; }}
                onClick={() => navigate(`/urun/${product.id}`)}
                className={`bg-white rounded-xl overflow-hidden border border-slate-100 hover:scale-[1.03] hover:-translate-y-1 transition-all duration-500 group cursor-pointer ${
                  visibleCards.has(i) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              >
                <div className="relative w-full h-48 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                  <span className="absolute top-3 left-3 bg-orange-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                    {product.category}
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-slate-900 text-sm mb-1 line-clamp-2">{product.name}</h3>
                  <p className="text-slate-500 text-xs leading-relaxed mb-3 line-clamp-3">{product.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xl font-extrabold text-orange-500">{product.price.toLocaleString('tr-TR')} ₺</span>
                    <span className="bg-slate-900 group-hover:bg-orange-500 text-white text-xs px-3 py-1.5 rounded-lg transition-colors duration-300 cursor-pointer whitespace-nowrap">
                      İncele
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default IkinciElPage;
